import { useCallback } from 'react'
import { useAngularServices } from '@/react/components'
import { ExternalAssignee } from '@/react/types/observations'

interface ApiError {
  response?: {
    data?: {
      email?: string[]
    }
  }
  data?: {
    email?: string[]
  }
}

interface ExternalAssigneeResponse {
  data: {
    id: number
    email: string
    created_date: string
    deleted: boolean
  }
}

interface UseExternalAssigneeAPIProps {
  answerId: number
  observationId?: number
  questionId?: number
  externalAssignees: ExternalAssignee[]
}

export const useExternalAssigneeAPI = ({
  answerId,
  observationId,
  questionId,
  externalAssignees,
}: UseExternalAssigneeAPIProps) => {
  const { Api } = useAngularServices()

  const processEmailAssignment = useCallback(
    async (email: string): Promise<{ success: boolean; error?: string }> => {
      try {
        // Check if there's an existing external assignee with this email that's marked as deleted
        const existingAssignee = externalAssignees.find(
          (assignee) =>
            assignee.external_contact?.email?.toLowerCase() ===
              email.toLowerCase() && assignee.deleted,
        )

        if (existingAssignee) {
          // Reactivate existing assignment
          await Api.patch(`answers_external_assignees/${existingAssignee.id}`, {
            deleted: false,
          })
        } else {
          // Create new external assignee and relationship
          const response = await Api.post('external_assignees', { email })

          // Type guard to safely access the response
          if (response && typeof response === 'object' && 'data' in response) {
            const data = response.data as ExternalAssigneeResponse['data']

            await Api.post('answers_external_assignees', {
              external_contact: data.id,
              date_created: data.created_date,
              email: data.email,
              deleted: data.deleted,
              answer: answerId,
            })
          }
        }

        return { success: true }
      } catch (error: unknown) {
        const apiError = error as ApiError
        const errorMessage =
          apiError?.response?.data?.email?.[0] ||
          apiError?.data?.email?.[0] ||
          `Failed to assign ${email}`

        return { success: false, error: errorMessage }
      }
    },
    [Api, answerId, externalAssignees],
  )

  const updateAnswerStatus = useCallback(async () => {
    if (observationId && questionId) {
      await Api.patch(`answers/${answerId}`, {
        answer: 'pr',
        observation: observationId,
        question: questionId,
      })
    }
  }, [Api, answerId, observationId, questionId])

  const updateAnswerSynchronization = useCallback(async (): Promise<void> => {
    await Api.post(`answers/${answerId}/synchronised`, {
      synchronised: true,
    })
  }, [Api, answerId])

  const processMultipleEmails = useCallback(
    async (emails: string[]): Promise<{ success: boolean; error?: string }> => {
      for (const email of emails) {
        const result = await processEmailAssignment(email)
        if (!result.success) {
          return result
        }
      }

      // Update synchronization after successful assignments
      if (emails.length > 0) {
        await updateAnswerSynchronization()
      }

      return { success: true }
    },
    [processEmailAssignment, updateAnswerSynchronization],
  )

  return {
    processEmailAssignment,
    processMultipleEmails,
    updateAnswerStatus,
    updateAnswerSynchronization,
  }
}
